import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { fetchPublicEvents, fetchPublicNews, type PublicEventItem, type PublicNewsItem } from '@/features/public/api';
import { formatEventDateTimeRange } from '@/shared/events/dateRange';
import { useRealtimeInvalidation } from '@/shared/realtime/useRealtimeInvalidation';

function toCategoryId(value: string) {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export default function NewsPage() {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [news, setNews] = useState<PublicNewsItem[]>([]);
  const [events, setEvents] = useState<PublicEventItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();
  const articlesPerPage = 6;

  const loadNews = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [newsData, eventsData] = await Promise.all([
        fetchPublicNews({ page: 1, limit: 100 }),
        fetchPublicEvents({ status: 'PUBLISHED', page: 1, limit: 100 })
      ]);
      setNews(newsData.items);
      setEvents(eventsData.items);
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Impossible de charger les actualités";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadNews();
  }, [loadNews]);

  useRealtimeInvalidation({
    topics: ["news", "events"],
    onInvalidate: () => {
      void loadNews();
    }
  });

  const filteredNews = useMemo(() => {
    if (selectedCategory === 'event') {
      return [];
    }

    return news.filter(item => {
      const matchesCategory = selectedCategory === 'all' || toCategoryId(item.categorie) === selectedCategory;
      const matchesSearch = item.titre.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           item.extrait.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [news, searchQuery, selectedCategory]);

  const filteredEvents = useMemo(() => {
    if (selectedCategory !== 'event') {
      return [];
    }

    return events.filter((item) => {
      const query = searchQuery.toLowerCase();
      return (
        item.titre.toLowerCase().includes(query) ||
        item.description.toLowerCase().includes(query) ||
        item.lieu.toLowerCase().includes(query)
      );
    });
  }, [events, searchQuery, selectedCategory]);

  const featuredNews = filteredNews.find((item) => item.featured) ?? filteredNews[0];
  const listNews = useMemo(
    () => filteredNews.filter((item) => item.id !== featuredNews?.id),
    [featuredNews?.id, filteredNews]
  );
  const totalPages = Math.max(1, Math.ceil((selectedCategory === 'event' ? filteredEvents.length : listNews.length) / articlesPerPage));
  const paginatedNews = useMemo(() => {
    const startIndex = (currentPage - 1) * articlesPerPage;
    return listNews.slice(startIndex, startIndex + articlesPerPage);
  }, [articlesPerPage, currentPage, listNews]);
  const paginatedEvents = useMemo(() => {
    const startIndex = (currentPage - 1) * articlesPerPage;
    return filteredEvents.slice(startIndex, startIndex + articlesPerPage);
  }, [articlesPerPage, currentPage, filteredEvents]);
  const newsCategories = useMemo(() => {
    const groupedCategories = new Map<string, { id: string; name: string; count: number }>();

    news.forEach((item) => {
      const key = toCategoryId(item.categorie);
      const current = groupedCategories.get(key);

      if (current) {
        current.count += 1;
      } else {
        groupedCategories.set(key, {
          id: key,
          name: item.categorie,
          count: 1
        });
      }
    });

    return [
      { id: 'all', name: 'Toutes', count: news.length },
      ...(events.length > 0 ? [{ id: 'event', name: 'Événement', count: events.length }] : []),
      ...Array.from(groupedCategories.values()).sort((left, right) => left.name.localeCompare(right.name, 'fr'))
    ];
  }, [events.length, news]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedCategory]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      {/* Hero */}
      <section className="relative py-12 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=african%20newspaper%20journalism%20media%20press%20senegal%20dakar%20modern%20editorial%20office%20vibrant%20orange%20colors%20professional&width=1600&height=300&seq=news-hero1&orientation=landscape"
            alt="Actualités ASC"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-[#E8521A]/85"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center text-white">
            <h1 className="text-3xl md:text-4xl font-bold mb-4">Actualités ASC</h1>
            <p className="text-lg opacity-90 max-w-2xl mx-auto">
              Restez informé de toute l'actualité du club, des événements sportifs aux annonces du bureau
            </p>
          </div>
        </div>
      </section>

      {selectedCategory !== 'event' ? (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8">
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            {loading ? (
              <div className="h-64 animate-pulse bg-gray-100"></div>
            ) : featuredNews ? (
              <div className="grid grid-cols-1 lg:grid-cols-2">
                <div className="relative h-64 lg:h-auto overflow-hidden">
                  <img
                    src={featuredNews.image ?? "https://readdy.ai/api/search-image?query=senegal%20africa%20news%20article%20professional%20photography%20editorial%20clean%20background&width=800&height=500&seq=news-main&orientation=landscape"}
                    alt={featuredNews.titre}
                    className="w-full h-full object-cover"
                    style={{
                      objectPosition: "center top",
                      transform: "translateY(48px) scale(1.08)",
                      transformOrigin: "center top",
                    }}
                  />
                  <span className="absolute top-14 left-4 z-10 bg-orange-500 text-white text-sm font-medium px-4 py-1 rounded-full shadow-md">
                    À la une
                  </span>
                </div>
                <div className="p-8 flex flex-col justify-center">
                  <span className="text-orange-600 font-medium text-sm mb-2">{featuredNews.categorie}</span>
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                    {featuredNews.titre}
                  </h2>
                  <p className="text-gray-600 mb-6">{featuredNews.extrait}</p>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-gray-500">{featuredNews.date}</span>
                    <button
                      onClick={() => navigate(`/actualites/${featuredNews.id}`)}
                      className="text-orange-600 font-medium hover:text-orange-700 flex items-center gap-1"
                    >
                      Lire l'article
                      <i className="ri-arrow-right-line"></i>
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-8 text-center text-gray-500">
                {error ?? "Aucune actualité publiée pour le moment."}
              </div>
            )}
          </div>
        </div>
      ) : null}

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Search */}
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                <input
                  type="text"
                  placeholder="Rechercher..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                />
              </div>
            </div>

            {/* Categories */}
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <h3 className="font-bold text-gray-900 mb-4">Catégories</h3>
              <div className="space-y-2">
                {newsCategories.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-left transition-colors ${
                      selectedCategory === cat.id
                        ? 'bg-orange-50 text-orange-600'
                        : 'hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    <span>{cat.name}</span>
                    <span className={`text-sm px-2 py-0.5 rounded-full ${
                      selectedCategory === cat.id ? 'bg-orange-200' : 'bg-gray-100'
                    }`}>
                      {cat.count}
                    </span>
                  </button>
                ))}
              </div>
            </div>

          </div>

          {/* Articles Grid */}
          <div className="lg:col-span-3">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">{selectedCategory === 'event' ? 'Événements en cours' : 'Nos articles'}</h2>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span>Trier par:</span>
                <select className="border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-orange-500">
                  <option>Plus récent</option>
                  <option>Plus ancien</option>
                  <option>Populaire</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {loading ? (
                Array.from({ length: 4 }).map((_, index) => (
                  <div key={index} className="h-64 rounded-xl bg-gray-100 animate-pulse"></div>
                ))
              ) : selectedCategory === 'event' ? (
                paginatedEvents.map((item) => {
                  const eventImage = item.image?.trim() ? item.image : null;

                  return (
                    <article key={item.id} className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-lg transition-shadow group">
                      <div className="relative h-48 overflow-hidden">
                        {eventImage ? (
                          <img
                            src={eventImage}
                            alt={item.titre}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        ) : (
                          <div className="relative h-full w-full bg-[linear-gradient(135deg,#1A8C7A_0%,#C98958_50%,#1A8C7A_100%)]">
                            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.18),transparent_30%),radial-gradient(circle_at_bottom_left,rgba(255,255,255,0.12),transparent_35%)]" />
                            <div className="absolute inset-x-0 bottom-0 p-5 text-white">
                              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-white/80">Événement</div>
                              <div className="mt-2 line-clamp-2 text-2xl font-bold leading-tight">{item.titre}</div>
                            </div>
                          </div>
                        )}
                        <span className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm text-gray-900 text-sm font-medium px-3 py-1 rounded-full">
                          Événement
                        </span>
                      </div>
                      <div className="p-5">
                        <div className="flex items-center gap-3 text-sm text-gray-500 mb-3">
                          <span>{formatEventDateTimeRange(item)}</span>
                        </div>
                        <h3 className="font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-orange-600 transition-colors">
                          {item.titre}
                        </h3>
                        <p className="text-gray-600 text-sm line-clamp-2 mb-2">
                          {item.description}
                        </p>
                        <p className="text-sm text-gray-500 mb-4">
                          <i className="ri-map-pin-line mr-1"></i>
                          {item.lieu}
                        </p>
                        <button
                          onClick={() => navigate(item.type === 'CULTURE' ? '/culture' : '/sport/evenements')}
                          className="text-orange-600 font-medium text-sm hover:text-orange-700 flex items-center gap-1"
                        >
                          Voir l'événement
                          <i className="ri-arrow-right-line"></i>
                        </button>
                      </div>
                    </article>
                  );
                })
              ) : paginatedNews.map(item => (
                  <article key={item.id} className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-lg transition-shadow group">
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={item.image ?? "https://readdy.ai/api/search-image?query=senegal%20africa%20news%20article%20professional%20photography%20editorial%20clean%20background&width=800&height=500&seq=news-list&orientation=landscape"}
                        alt={item.titre}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        style={{ objectPosition: "center 22%" }}
                      />
                      <span className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm text-gray-900 text-sm font-medium px-3 py-1 rounded-full">
                        {item.categorie}
                      </span>
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-3 text-sm text-gray-500 mb-3">
                        <span>{item.date}</span>
                      </div>
                      <h3 className="font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-orange-600 transition-colors">
                        {item.titre}
                      </h3>
                      <p className="text-gray-600 text-sm line-clamp-2 mb-4">
                        {item.extrait}
                      </p>
                      <button
                        onClick={() => navigate(`/actualites/${item.id}`)}
                        className="text-orange-600 font-medium text-sm hover:text-orange-700 flex items-center gap-1"
                      >
                        Lire la suite
                        <i className="ri-arrow-right-line"></i>
                      </button>
                    </div>
                  </article>
                ))}
            </div>

            {!loading && ((selectedCategory === 'event' && filteredEvents.length === 0) || (selectedCategory !== 'event' && filteredNews.length === 0)) && (
              <div className="text-center py-16">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-article-line text-3xl text-gray-400"></i>
                </div>
                <p className="text-gray-500">{error ?? (selectedCategory === 'event' ? "Aucun événement trouvé" : "Aucun article trouvé")}</p>
              </div>
            )}

            {/* Pagination */}
            {((selectedCategory === 'event' && filteredEvents.length > 0) || (selectedCategory !== 'event' && listNews.length > 0)) && (
              <div className="flex items-center justify-center gap-2 mt-8">
                <button
                  onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                  disabled={currentPage === 1}
                  className="w-10 h-10 rounded-lg bg-orange-500 text-white flex items-center justify-center hover:bg-orange-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <i className="ri-arrow-left-line"></i>
                </button>
                <div className="min-w-[110px] h-10 px-4 rounded-lg bg-orange-500 text-white flex items-center justify-center text-sm font-semibold">
                  {totalPages > 1 ? `1 ... ${totalPages}` : '1'}
                </div>
                <button
                  onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                  disabled={currentPage === totalPages}
                  className="w-10 h-10 rounded-lg bg-orange-500 text-white flex items-center justify-center hover:bg-orange-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
